// src/services/api.js
import axios from 'axios';
import { formatDateForAPI, formatDateForInput } from '@/utils/dateUtils';

// Create an axios instance with the base URL
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 10000
});


// Add request interceptor for authentication
apiClient.interceptors.request.use(
  config => {
    // For JWT from djangorestframework-simplejwt, the token is stored as access token
    const token = localStorage.getItem('access_token');
    if (token) {
      // The proper format for JWT is "Bearer <token>"
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Helper functions for date handling - now using centralized dateUtils
export { formatDateForAPI };  // Re-export from dateUtils

// Format dates from API to display correctly
export const formatDateFromAPI = (dateString) => {
  if (!dateString) return '';

  // Return as is, the issue is only when sending to the API
  return dateString;
};

// Export specific API services

// Patient Service
export const patientService = {
  getAll(params = {}) {
    return apiClient.get('/patients/', { params });
  },
  get(id) {
    return apiClient.get(`/patients/${id}/`);
  },
  create(data) {
    return apiClient.post('/patients/', data);
  },
  update(id, data) {
    return apiClient.put(`/patients/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/patients/${id}/`);
  },
  search(searchTerm) {
    return apiClient.get('/patients/', {
      params: { search: searchTerm }
    });
  },
  getByStatus(status) {
    return apiClient.get('/patients/', {
      params: { status: status }
    });
  }
};

// Appointment Service with date handling
export const appointmentService = {
  getAll(params = {}) {
    return apiClient.get('/appointments/', { params });
  },
  get(id) {
    return apiClient.get(`/appointments/${id}/`);
  },
  getByPatient(patientId) {
    return apiClient.get('/appointments/', {
      params: { patient: patientId }
    });
  },
  getByDateRange(startDate, endDate) {
    return apiClient.get('/appointments/', {
      params: {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate)
      }
    });
  },
  getWithPatientData(limit = 5) {
    return apiClient.get('/appointments/', {
      params: {
        limit,
        include_patient_data: true,
        ordering: '-fecha'
      }
    });
  },
  create(data) {
    // Create a copy of the data to avoid modifying the original
    const formattedData = { ...data };

    // Format the date for API submission using centralized utility
    if (formattedData.fecha) {
      formattedData.fecha = formatDateForAPI(formattedData.fecha);
    }

    return apiClient.post('/appointments/', formattedData);
  },
  update(id, data) {
    // Create a copy of the data to avoid modifying the original
    const formattedData = { ...data };

    // Format the date for API submission using centralized utility
    if (formattedData.fecha) {
      formattedData.fecha = formatDateForAPI(formattedData.fecha);
    }

    return apiClient.put(`/appointments/${id}/`, formattedData);
  },
  delete(id) {
    return apiClient.delete(`/appointments/${id}/`);
  }
};

// Clinical History Service
export const clinicalHistoryService = {
  getAll(params = {}) {
    return apiClient.get('/clinical-histories/', { params });
  },
  get(id) {
    return apiClient.get(`/clinical-histories/${id}/`);
  },
  getByPatient(patientId) {
    return apiClient.get('/clinical-histories/', {
      params: { patient: patientId }
    });
  },
  create(data) {
    return apiClient.post('/clinical-histories/', data);
  },
  update(id, data) {
    return apiClient.put(`/clinical-histories/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/clinical-histories/${id}/`);
  }
};

// Clinical Exam Service
export const clinicalExamService = {
  getAll(params = {}) {
    return apiClient.get('/clinical-exams/', { params });
  },
  get(id) {
    return apiClient.get(`/clinical-exams/${id}/`);
  },
  getByClinicalHistory(clinicalHistoryId) {
    return apiClient.get('/clinical-exams/', {
      params: { clinical_history: clinicalHistoryId }
    });
  },
  getByPatient(patientId) {
    return apiClient.get('/clinical-exams/', {
      params: { patient: patientId }
    });
  },
  create(data) {
    return apiClient.post('/clinical-exams/', data);
  },
  update(id, data) {
    return apiClient.put(`/clinical-exams/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/clinical-exams/${id}/`);
  }
};

// Odontogram Service
export const odontogramService = {
  getAll(params = {}) {
    return apiClient.get('/odontograms/', { params });
  },
  get(id) {
    return apiClient.get(`/odontograms/${id}/`);
  },
  getByClinicalHistory(clinicalHistoryId) {
    return apiClient.get('/odontograms/', {
      params: { clinical_history: clinicalHistoryId }
    });
  },
  getByPatient(patientId) {
    return apiClient.get('/odontograms/', {
      params: { patient: patientId }
    });
  },
  create(data) {
    return apiClient.post('/odontograms/', data);
  },
  update(id, data) {
    return apiClient.put(`/odontograms/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/odontograms/${id}/`);
  }
};

// Tooth Service
export const toothService = {
  getAll(params = {}) {
    return apiClient.get('/teeth/', { params });
  },
  get(id) {
    return apiClient.get(`/teeth/${id}/`);
  },
  getByOdontogram(odontogramId) {
    return apiClient.get('/teeth/', {
      params: { odontogram: odontogramId }
    });
  },
  getByToothNumber(toothNumber) {
    return apiClient.get('/teeth/', {
      params: { tooth_number: toothNumber }
    });
  },
  create(data) {
    return apiClient.post('/teeth/', data);
  },
  update(id, data) {
    return apiClient.put(`/teeth/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/teeth/${id}/`);
  }
};

// Dashboard Service
export const dashboardService = {
  getData(params = {}) {
    return apiClient.get('/dashboard/', { params });
  },
  getRecentItems(limit = 5) {
    return apiClient.get('/dashboard/recent/', {
      params: { limit }
    });
  }
};

// Clinical Image Service
export const clinicalImageService = {
  getAll(params = {}) {
    return apiClient.get('/clinical-images/', { params });
  },
  getByClinicalHistory(historyId) {
    return apiClient.get('/clinical-images/by_clinical_history/', {
      params: { clinical_history_id: historyId }
    });
  },
  getByImageType(imageType) {
    return apiClient.get('/clinical-images/', {
      params: { image_type: imageType }
    });
  },
  getByDateRange(startDate, endDate) {
    return apiClient.get('/clinical-images/', {
      params: { 
        upload_date_start: formatDateForAPI(startDate),
        upload_date_end: formatDateForAPI(endDate)
      }
    });
  },
  get(id) {
    return apiClient.get(`/clinical-images/${id}/`);
  },
  create(formData) {
    return apiClient.post('/clinical-images/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  update(id, formData) {
    return apiClient.put(`/clinical-images/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  delete(id) {
    return apiClient.delete(`/clinical-images/${id}/`);
  }
};

// Authentication Service
export const authService = {
  login(credentials) {
    return apiClient.post('/token/', credentials)
      .then(response => {
        // Store both tokens
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        return response;
      });
  },
  getUserInfo() {
    return apiClient.get('/user-info/');
  },

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  },
  // Add method to refresh token when it expires
  refreshToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return Promise.reject('No refresh token available');

    return apiClient.post('/token/refresh/', { refresh: refreshToken })
      .then(response => {
        localStorage.setItem('access_token', response.data.access);
        return response;
      });
  }
};

// Product Service
export const productService = {
  getAll(params = {}) {
    return apiClient.get('/products/', { params });
  },
  get(id) {
    return apiClient.get(`/products/${id}/`);
  },
  create(data) {
    return apiClient.post('/products/', data);
  },
  update(id, data) {
    return apiClient.put(`/products/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/products/${id}/`);
  },
  getOperations(id) {
    return apiClient.get(`/products/${id}/operations/`);
  }
};

// Reminder Service
export const reminderService = {
  getAll(params = {}) {
    return apiClient.get('/reminders/', { params });
  },
  get(id) {
    return apiClient.get(`/reminders/${id}/`);
  },
  create(data) {
    return apiClient.post('/reminders/', data);
  },
  update(id, data) {
    return apiClient.put(`/reminders/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/reminders/${id}/`);
  }
};


// Supplier Service
export const supplierService = {
  getAll(params = {}) {
    return apiClient.get('/suppliers/', { params });
  },
  get(id) {
    return apiClient.get(`/suppliers/${id}/`);
  },
  create(data) {
    return apiClient.post('/suppliers/', data);
  },
  update(id, data) {
    return apiClient.put(`/suppliers/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/suppliers/${id}/`);
  },
  getOperations(id) {
    return apiClient.get(`/suppliers/${id}/operations/`);
  }
};

// Inventory Operation Service
export const inventoryOperationService = {
  getAll(params = {}) {
    return apiClient.get('/inventory-operations/', { params });
  },
  get(id) {
    return apiClient.get(`/inventory-operations/${id}/`);
  },
  create(data) {
    // Format the date for API submission
    const formattedData = { ...data };
    if (formattedData.date) {
      formattedData.date = formatDateForAPI(formattedData.date);
    }

    return apiClient.post('/inventory-operations/', formattedData);
  },
  update(id, data) {
    // Format the date for API submission
    const formattedData = { ...data };
    if (formattedData.date) {
      formattedData.date = formatDateForAPI(formattedData.date);
    }

    return apiClient.put(`/inventory-operations/${id}/`, formattedData);
  },
  delete(id) {
    return apiClient.delete(`/inventory-operations/${id}/`);
  },
  getSummary() {
    return apiClient.get('/inventory-operations/summary/');
  }
};

export const inventoryService = {
  getAll(params = {}) {
    return apiClient.get('/inventory/', { params });
  },
  get(id) {
    return apiClient.get(`/inventory/${id}/`);
  },
  getByDateRange(startDate, endDate) {
    return apiClient.get('/inventory/', {
      params: {
        last_check_start: formatDateForAPI(startDate),
        last_check_end: formatDateForAPI(endDate)
      }
    });
  },
  getByCheckedBy(checkedBy) {
    return apiClient.get('/inventory/', {
      params: { checked_by: checkedBy }
    });
  },
  create(data) {
    return apiClient.post('/inventory/', data);
  },
  update(id, data) {
    return apiClient.put(`/inventory/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/inventory/${id}/`);
  }
};

export const paymentService = {
  getAll(params = {}) {
    return apiClient.get('/payments/', { params });
  },
  getByPatient(patientId) {
    return apiClient.get('/payments/', {
      params: { patient: patientId }
    });
  },
  getByBudget(budgetId) {
    return apiClient.get('/payments/', {
      params: { budget: budgetId }
    });
  },
  getByDateRange(startDate, endDate) {
    return apiClient.get('/payments/', {
      params: {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate)
      }
    });
  },
  get(id) {
    return apiClient.get(`/payments/${id}/`);
  },
  create(data) {
    return apiClient.post('/payments/', data);
  },
  update(id, data) {
    return apiClient.put(`/payments/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/payments/${id}/`);
  }
};

export const googleCalendarService = {
  /**
   * Get the Google authorization URL
   * @returns {Promise} Promise with the authorization URL
   */
  getAuthUrl() {
    return apiClient.get('/google/auth/');
  },

  /**
   * Check if an appointment is synced with Google Calendar
   * @param {number} appointmentId - The appointment ID to check
   * @returns {Promise} Promise with the sync status
   */
  checkSync(appointmentId) {
    return apiClient.get(`/google/check-sync/${appointmentId}/`);
  },

  /**
   * Sync an appointment with Google Calendar
   * @param {number} appointmentId - The appointment ID to sync
   * @returns {Promise} Promise with the sync result
   */
  syncAppointment(appointmentId) {
    return apiClient.post('/google/sync/', { appointment_id: appointmentId });
  },

  /**
   * Delete an appointment from Google Calendar
   * @param {number} appointmentId - The appointment ID to delete
   * @returns {Promise} Promise with the deletion result
   */
  deleteFromCalendar(appointmentId) {
    return apiClient.post('/google/delete-event/', { appointment_id: appointmentId });
  },

  /**
   * Sync all upcoming appointments with Google Calendar
   * @returns {Promise} Promise with the sync results
   */
  syncAllAppointments() {
    return apiClient.post('/google/sync-all/');
  },

  checkAuthStatus() {
    return apiClient.get('/google/auth-status/');
  }
};

// Treatment Plans Service
export const treatmentPlanService = {
  getAll(params = {}) {
    return apiClient.get('/treatment-plans/', { params });
  },

  get(id) {
    return apiClient.get(`/treatment-plans/${id}/`);
  },

  create(data) {
    return apiClient.post('/treatment-plans/', data);
  },

  update(id, data) {
    return apiClient.put(`/treatment-plans/${id}/`, data);
  },

  delete(id) {
    return apiClient.delete(`/treatment-plans/${id}/`);
  },

  // Get treatment plans for a specific patient
  getByPatient(patientId) {
    return apiClient.get('/treatment-plans/', {
      params: { patient: patientId }
    });
  }
};

export const budgetService = {
  getAll(params = {}) {
    return apiClient.get('/budgets/', { params });
  },
  get(id) {
    return apiClient.get(`/budgets/${id}/`);
  },
  getByPatient(patientId) {
    return apiClient.get('/budgets/', {
      params: { patient: patientId }
    });
  },
  create(data) {
    return apiClient.post('/budgets/', data);
  },
  update(id, data) {
    return apiClient.put(`/budgets/${id}/`, data);
  },
  delete(id) {
    return apiClient.delete(`/budgets/${id}/`);
  },
  getPayments(id) {
    return apiClient.get(`/budgets/${id}/payments/`);
  }
};

export const estadisticasService = {
  // Obtener pagos agrupados por mes para un año específico
  getPaymentsByMonth(year) {
    return apiClient.get('/estadisticas/pagos-por-mes/', {
      params: { year }
    });
  },

  // Obtener planes de tratamiento agrupados por mes para un año específico
  getTreatmentPlansByMonth(year) {
    return apiClient.get('/estadisticas/tratamientos-por-mes/', {
      params: { year }
    });
  },
  
  // Get appointments grouped by month for a specific year
  getAppointmentsByMonth(year) {
    return apiClient.get('/estadisticas/citas-por-mes/', {
      params: { year }
    });
  }
};

// Export the axios instance for custom requests
export default apiClient;